import { useMemo } from 'react';
import { Flex, Image } from '@qga/roo-ui/components';

import { useIsInView } from '@shared/v2/components/InView';
import { BEST_RATE_ID } from '@shared/v2/constants/packages';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import Heading from '@/components/Heading';
import usePropertyContent from '@/hooks/usePropertyContent';
import Modal, { AnimatedModal } from '@/v2/components/layout/Modal';
import { useOtherPackageModal } from '@/v2/components/pages/PackageViewV2/contexts/OtherPackageModalContext';
import { OtherPackageOffer } from '@/v2/components/pages/PackageViewV2/contexts/OtherPackageModalContext/utils';
import { UI } from '@/v2/types';
import { useRoomOptionsData } from '../../contexts/RoomOptionsContext';
import { otherPackageModalDataLayerEvent } from '../../utils/dataLayer';
import BestAvailableRateCard from './components/BestAvailableRateCard';
import Info from './components/Info';
import PackageOptionCard from './components/PackageOptionCard';
import RoomType from './components/RoomType';

export type OtherPackageModalProps = {
  isOpen: boolean;
  onClose: (roomId?: string) => void;
};

const OtherPackageModal = ({ isOpen, onClose }: OtherPackageModalProps) => {
  const { setSearchQuery, searchQuery } = useSearchQuery();
  const { scrollIntoView } = useIsInView('package');
  const {
    isPreSearch,
    setIsPackageOptionOpen,
    onCtaClick = () => {},
    isCampaignAvailable,
    showCampaignPackageOptionVariants,
  } = useRoomOptionsData() || {};
  const propertyContent = usePropertyContent() || {};

  const {
    isReady,
    selectedOtherRoomOption,
    selectedPackageOption,
    lastOpenedQuery,
    route,
    offers,
    didModalAutoOpen,
  } = useOtherPackageModal({ shouldAutoOpen: true });
  const isAlternativeModal =
    !isPreSearch && lastOpenedQuery && !selectedOtherRoomOption;

  const alwaysRequired = !!(isReady && route && selectedPackageOption);

  // Alternative Modal does not require a room to be selected,
  // but does require that at least 1 post-search offer exists.
  const isBodyReady =
    alwaysRequired &&
    (isAlternativeModal
      ? offers?.length > 0
      : !!selectedOtherRoomOption?.roomType.id);

  const {
    mainImage,
    name: roomName = '',
    id: roomId = '',
    maxOccupants,
  } = selectedOtherRoomOption?.roomType || {};

  const modalHeader = isAlternativeModal
    ? 'Change package option'
    : 'Room package options';

  const handleSelectPackageOption = (selectedPackage?: {
    packageOption?: { id?: string };
    roomId?: string;
  }) => {
    const { packageOption, roomId: selectedPackageRoomId } =
      selectedPackage || {};

    const selectedRoomId = roomId || selectedPackageRoomId;

    const shouldTurnOffCampaign =
      !isCampaignAvailable &&
      showCampaignPackageOptionVariants &&
      selectedPackageRoomId;

    setIsPackageOptionOpen?.(false);
    setSearchQuery({
      ...(shouldTurnOffCampaign && { noCampaign: 'true' }),
      packageOption: packageOption?.id || BEST_RATE_ID,
      room: selectedRoomId,
    }).then(() => {
      scrollIntoView();

      const isPostSearchWithoutValidOffer = !isPreSearch && !offers?.[0]?.offer;
      if (isPreSearch || isPostSearchWithoutValidOffer) {
        onCtaClick('CHANGE_DATES', {
          roomOption: selectedOtherRoomOption!,
          shouldScrollIntoView: false,
          ctaText: 'Select and continue',
          ctaVariant: `${modalHeader} modal`,
        });
      }

      otherPackageModalDataLayerEvent({
        variant: didModalAutoOpen
          ? 'change_package_options_modal'
          : 'room_package_options_modal',
        action: 'select',
        propertyContent,
        searchQuery,
        roomId: selectedRoomId,
      });
    });

    onClose(selectedRoomId);
  };

  const orderedOffers = useMemo(() => {
    if (!offers) return [];

    let standardOffer: OtherPackageOffer | undefined;
    let poOffers = [];

    // Move Standard Rate offer to the bottom
    if (isPreSearch) {
      standardOffer = offers.find((o) => !o.leadInOffer);
      poOffers = offers.filter((o) => o.leadInOffer);
    } else {
      standardOffer = offers?.find((o) => !o.packageOption);
      poOffers = offers?.filter((o) => o.packageOption);
    }

    return [...poOffers, standardOffer].filter(Boolean) as OtherPackageOffer[];
  }, [isPreSearch, offers]);

  const modalBody = isBodyReady ? (
    <>
      {!isAlternativeModal && (
        <Flex
          backgroundColor="white"
          p={['4', null, '5']}
          borderBottom={1}
          borderColor="border"
        >
          {mainImage && (
            <Image
              src={mainImage.small}
              alt={mainImage.caption}
              width="91px"
              height="57px"
              mr="6"
            />
          )}
          <RoomType name={roomName} maxOccupants={maxOccupants} />
        </Flex>
      )}
      <Flex mt="16px" flexDirection="column" gap="16px" px={['4', null, '5']}>
        <Info
          offers={orderedOffers}
          packageOption={selectedPackageOption}
          isWarning={isAlternativeModal}
        />
        <Heading.h6 color="text.body">
          {isPreSearch ? 'Package options' : 'Package options for your dates'}
        </Heading.h6>
        <Flex flexDirection={['column', null, 'row']} gap="6">
          <Flex
            width="100%"
            gap={4}
            flexDirection="column"
            alignItems="flex-start"
          >
            {orderedOffers && orderedOffers.length > 0 ? (
              orderedOffers.map((offer, idx) => {
                return (
                  <PackageOptionCard
                    key={idx}
                    route={route}
                    isPreSearch={isPreSearch}
                    offer={offer}
                    onSelectPackageOption={handleSelectPackageOption}
                  />
                );
              })
            ) : (
              <BestAvailableRateCard
                onSelectPackageOption={handleSelectPackageOption}
              />
            )}
          </Flex>
        </Flex>
        <Heading.h6 color="text.body">Your current package option</Heading.h6>
        <Flex
          width="100%"
          gap={4}
          flexDirection="column"
          alignItems="flex-start"
        >
          <PackageOptionCard
            route={route}
            isPreSearch={isPreSearch}
            offer={{
              offer: undefined,
              leadInOffer: selectedPackageOption?.cheapestOffer
                .leadInOffer as UI.RoomOptionOffer['leadInOffer'],
              packageOption: selectedPackageOption,
            }}
            isNotSelectable
            isAlternativeModal={isAlternativeModal}
          />
        </Flex>
      </Flex>
    </>
  ) : (
    <></>
  );

  return (
    <AnimatedModal size="lg" onClose={onClose} isOpen={isOpen && isBodyReady}>
      <Modal.Header>{modalHeader}</Modal.Header>
      <Modal.Body pb={['4', null, '6']} backgroundColor="background.page">
        {modalBody}
      </Modal.Body>
    </AnimatedModal>
  );
};
export default OtherPackageModal;
